/**
 * <navera-slider>: a small scroll-snap carousel shared by Navera custom sections
 * (hero slideshow, reviews, collection circles).
 *
 * Markup contract:
 *   <navera-slider data-autoplay="5" data-loop>
 *     <div data-slider-track> ...slides (direct children)... </div>
 *     <button data-slider-prev> / <button data-slider-next>   (optional)
 *     <button data-slider-dot="0"> ...                          (optional)
 *   </navera-slider>
 *
 * Native scrolling does the moving (swipe, trackpad, keyboard), so the slider still works
 * if this script fails to load. Autoplay pauses on hover, focus, when off screen, when the
 * tab is hidden, and never runs under prefers-reduced-motion.
 */
class NaveraSlider extends HTMLElement {
  #track = null;
  #slides = [];
  #index = 0;
  #timer = 0;
  #paused = false;
  #visible = true;
  #frame = 0;
  #observer = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-slider-track]');
    if (!this.#track) return;
    this.#slides = Array.from(this.#track.children);

    this.querySelector('[data-slider-prev]')?.addEventListener('click', () => this.go(this.#index - 1, true));
    this.querySelector('[data-slider-next]')?.addEventListener('click', () => this.go(this.#index + 1, true));
    this.querySelectorAll('[data-slider-dot]').forEach((dot) => {
      dot.addEventListener('click', () => this.go(Number(dot.dataset.sliderDot), true));
    });

    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibility);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#resume);

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
      this.#schedule();
    });
    this.#observer.observe(this);

    this.#update();
    this.#schedule();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    cancelAnimationFrame(this.#frame);
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#resume);
  }

  get #autoplaySeconds() {
    const seconds = Number(this.dataset.autoplay || 0);
    if (!seconds || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
    return seconds;
  }

  get #atEnd() {
    const track = this.#track;
    return track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
  }

  /**
   * Scrolls to a slide. Wraps around when data-loop is set (or when autoplaying).
   * @param {number} index
   * @param {boolean} [fromUser] - User-initiated moves restart the autoplay timer.
   */
  go(index, fromUser = false) {
    const count = this.#slides.length;
    if (!count) return;

    const loop = this.hasAttribute('data-loop') || this.#autoplaySeconds > 0;
    if (index >= count || (index > this.#index && this.#atEnd)) {
      index = loop ? 0 : this.#index;
    } else if (index < 0) {
      index = loop ? count - 1 : 0;
    }

    const slide = this.#slides[index];
    if (!slide) return;
    this.#track.scrollTo({ left: slide.offsetLeft - this.#track.offsetLeft, behavior: 'smooth' });
    this.#index = index;
    this.#update();
    if (fromUser) this.#schedule();
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const left = this.#track.scrollLeft;
      let closest = 0;
      let distance = Infinity;
      this.#slides.forEach((slide, i) => {
        const d = Math.abs(slide.offsetLeft - this.#track.offsetLeft - left);
        if (d < distance) {
          distance = d;
          closest = i;
        }
      });
      this.#index = closest;
      this.#update();
    });
  };

  #update() {
    this.querySelectorAll('[data-slider-dot]').forEach((dot) => {
      const active = Number(dot.dataset.sliderDot) === this.#index;
      dot.toggleAttribute('aria-current', active);
    });
    this.#slides.forEach((slide, i) => slide.toggleAttribute('data-active', i === this.#index));

    if (!this.hasAttribute('data-loop') && this.#autoplaySeconds === 0) {
      const prev = this.querySelector('[data-slider-prev]');
      const next = this.querySelector('[data-slider-next]');
      if (prev) prev.disabled = this.#track.scrollLeft <= 2;
      if (next) next.disabled = this.#atEnd;
    }
  }

  #schedule() {
    window.clearTimeout(this.#timer);
    const seconds = this.#autoplaySeconds;
    if (!seconds || this.#paused || !this.#visible || document.hidden || this.#slides.length < 2) return;
    this.#timer = window.setTimeout(() => this.go(this.#index + 1), seconds * 1000);
  }

  #pause = () => {
    this.#paused = true;
    window.clearTimeout(this.#timer);
  };

  #resume = () => {
    this.#paused = false;
    this.#schedule();
  };

  #onVisibility = () => this.#schedule();

  /** Theme editor: selecting a slide block scrolls to it and holds autoplay. */
  #onBlockSelect = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const index = this.#slides.findIndex((slide) => slide === target || slide.contains(target));
    if (index === -1) return;
    this.#pause();
    this.go(index);
  };
}

if (!customElements.get('navera-slider')) {
  customElements.define('navera-slider', NaveraSlider);
}
